import { DirectusErrorCode } from '@funnellab/shared/src/types/errorCodes.ts';

export interface DirectusError {
  extensions: {
    code: DirectusErrorCode;
  };
  message: string;
}

export type MaybeDirectusError = Partial<DirectusError> & {
  extensions?: {
    code?: DirectusErrorCode | string | number;
  };
};

export const isDirectusError = (error: unknown): error is DirectusError => {
  const code = (error as MaybeDirectusError).extensions?.code;
  return Object.prototype.hasOwnProperty.call(
    DirectusErrorCode,
    code as string,
  );
};

export type DirectusErrorArray = { errors: DirectusError[] };
export const isDirectusErrorArray = (
  error: unknown,
): error is DirectusErrorArray => {
  return (
    !!error &&
    Object.prototype.hasOwnProperty.call(error, 'errors') &&
    Array.isArray((error as { errors: unknown }).errors) &&
    (error as { errors: unknown[] }).errors.every(isDirectusError)
  );
};

export type CustomDirectusError = {
  code: string;
  name: string;
  status: number;
};

export const extractCustomDirectusErrors = (
  error: unknown,
): CustomDirectusError[] => {
  try {
    const errorObj = JSON.parse(
      (error as { errors: string }).errors,
    ) as unknown;
    if (Array.isArray(errorObj)) {
      return errorObj as {
        name: string;
        code: string;
        status: number;
      }[];
    } else {
      return [
        errorObj as {
          name: string;
          code: string;
          status: number;
        },
      ];
    }
  } catch {
    return [];
  }
};

export const collectErrorsForDisplay = (
  error: unknown,
  callback: (codes: DirectusErrorCode[]) => void,
): void => {
  callback(collectErrorsForDisplaySimple(error));
};

export const collectErrorsForDisplaySimple = (
  error: unknown,
): DirectusErrorCode[] => {
  if (isDirectusErrorArray(error) && error.errors[0]) {
    return error.errors.map((e) => e.extensions.code);
  }
  if (error && isDirectusError(error)) {
    return [error.extensions.code];
  }
  const customDirectusErrors = extractCustomDirectusErrors(error);
  if (customDirectusErrors[0]) {
    return customDirectusErrors.map((e) => e.code as DirectusErrorCode);
  }
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return [DirectusErrorCode.NETWORK_ERROR];
  }
  return [DirectusErrorCode.unknown];
};
